import { MAX_FLOW_ELEMENT_IDS } from "@redotech/customer-portal-sdk/rpc/schema/get-flow-element-text-overlay";
import { DEFAULT_LOCALE, useTranslation } from "@redotech/redo-i18n";
import { FlowElementTextOverlay } from "@redotech/redo-return-core-common/return-flow/flow-element-translation";
import {
  type LocalizationSettings,
  localizationEnabled,
} from "@redotech/redo-team-common/localization/settings";
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { RpcClientContext } from "./contexts/rpc";

const EMPTY_FLOW_ELEMENT_TEXT_OVERLAY: FlowElementTextOverlay = {};

export function useFlowElementTextOverlay({
  elementIds,
  localization,
}: {
  elementIds: readonly string[];
  localization: LocalizationSettings | null | undefined;
}): FlowElementTextOverlay {
  const client = useContext(RpcClientContext);
  const { i18n } = useTranslation();
  const language = i18n.language.split("-")[0] ?? i18n.language;
  const requestedElementIds = [...new Set(elementIds)].slice(
    0,
    MAX_FLOW_ELEMENT_IDS,
  );
  const elementIdsKey = requestedElementIds.join("\n");
  const enabled =
    !!client &&
    localizationEnabled(localization) &&
    language !== DEFAULT_LOCALE &&
    requestedElementIds.length > 0;
  const query = useQuery({
    queryKey: ["flowElementTextOverlay", language, elementIdsKey],
    queryFn: async () => {
      if (!client) {
        return EMPTY_FLOW_ELEMENT_TEXT_OVERLAY;
      }
      const { overlay } = await client.getFlowElementTextOverlay({
        elementIds: requestedElementIds,
        language,
      });
      return overlay;
    },
    enabled,
    staleTime: Infinity,
  });

  return enabled
    ? (query.data ?? EMPTY_FLOW_ELEMENT_TEXT_OVERLAY)
    : EMPTY_FLOW_ELEMENT_TEXT_OVERLAY;
}
